import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Activity, Play, RefreshCw, RotateCcw, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/research-services`;
const authorization = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });

export const ResearchServicesPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/status`, authorization());
      setStatus(response.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load research service status.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const perform = async (action) => {
    setBusy(action);
    try {
      if (action === "refresh") await load();
      else {
        const response = await axios.post(`${API}/${action}`, {}, authorization());
        if (action === "check") {
          const ok = Object.values(response.data.results).every(result => result.ok);
          toast[ok ? "success" : "error"](ok ? "Both research services are connected." : "A service check failed. See details below.");
        } else toast.success("Queue processing requested.");
        await load();
      }
    } catch (err) {
      const message = err.response?.data?.detail || "The action could not be completed.";
      setError(message);
      toast.error(message);
    } finally { setBusy(""); }
  };
  return (
    <section className="border-y border-slate-200 py-6" data-testid="research-services-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h3 className="text-lg font-semibold flex items-center gap-2"><Activity className="w-5 h-5 text-orange-600" /> Research services & workers</h3>
        <Button type="button" size="sm" variant="ghost" title="Refresh service status" aria-label="Refresh service status" data-testid="research-services-refresh" disabled={!!busy} onClick={() => perform("refresh")}>
          <RefreshCw className={`h-4 w-4 ${busy === "refresh" ? "animate-spin" : ""}`} />
        </Button>
      </div>
      {error && <p role="alert" className="text-sm text-red-700 mb-4 break-words" data-testid="research-services-error">{error}</p>}
      {!status && !error && <p data-testid="research-services-loading" className="text-sm text-slate-500">Loading service status…</p>}
      {status && <>
        {status.configuration.missing.length > 0 && <p role="alert" className="text-sm text-red-700 mb-4 break-all" data-testid="research-services-missing">Missing settings: {status.configuration.missing.join(", ")}</p>}
        <div className="grid sm:grid-cols-2 gap-5 mb-5">
          {[["openalex", "OpenAlex"], ["unpaywall", "Unpaywall"]].map(([key, name]) => {
            const result = status.checks?.[key];
            return <div key={key} className="min-w-0" data-testid={`service-${key}`}>
              <div className="text-sm font-semibold">{name}</div>
              <p className={`text-sm mt-1 break-words ${result?.ok ? "text-green-700" : result ? "text-red-700" : "text-slate-500"}`} data-testid={`service-${key}-status`}>{result?.message || "Not tested yet"}</p>
              <p className="text-xs text-slate-500 break-all mt-1" data-testid={`service-${key}-configuration`}>{key === "openalex" ? (status.configuration.openalex_key_configured ? "API key configured · server-side" : "API key missing") : status.configuration.unpaywall_email || "Contact email missing"}</p>
            </div>;
          })}
        </div>
        <p className="text-xs text-slate-500 mb-4" data-testid="service-check-time">{status.checked_at ? `Last checked: ${new Date(status.checked_at).toLocaleString()}` : "No connection check recorded"}</p>
        <div className="flex flex-wrap gap-2 mb-6">
          <Button type="button" variant="outline" disabled={!!busy} data-testid="research-services-check" onClick={() => perform("check")}><ShieldCheck className="w-4 h-4 mr-2" />{busy === "check" ? "Checking…" : "Test connections"}</Button>
          <Button type="button" variant="outline" disabled={!!busy || status.configuration.missing.length > 0} data-testid="research-services-run" onClick={() => perform("run")}><Play className="w-4 h-4 mr-2" />Run pending jobs</Button>
          {status.queues.oa_pdf.failed > 0 && <Button type="button" variant="outline" disabled={!!busy} data-testid="research-services-retry" onClick={() => perform("retry-imports")}><RotateCcw className="w-4 h-4 mr-2" />Retry failed imports</Button>}
        </div>
        <div className="space-y-4">
          {[["oa_pdf", "Imports / PDF fetching"], ["ocr", "OCR / indexing"]].map(([key, name]) => <div key={key} data-testid={`worker-${key}`}>
            <div className="text-sm font-semibold mb-2">{name}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(status.queues[key]).map(([state, count]) => <p key={state} className="text-sm text-slate-600 capitalize" data-testid={`worker-${key}-${state}`}>{state}: <strong className="text-slate-900">{count}</strong></p>)}
            </div>
          </div>)}
        </div>
        <p className="text-xs text-slate-500 mt-4" data-testid="worker-recovery-status">{status.configuration.cron_secret_configured ? "Scheduled recovery configured" : "Scheduled recovery not configured"} · Every 15 minutes · 1 active job per queue</p>
        {status.recent_imports.length > 0 && <div className="mt-5 divide-y divide-slate-200" data-testid="recent-import-jobs">
          {status.recent_imports.map(job => <div key={job.id} className="py-3 min-w-0" data-testid={`import-job-${job.id}`}>
            <p className="text-sm font-medium break-words" data-testid={`import-job-title-${job.id}`}>{job.title}</p>
            <p className="text-xs text-slate-500 mt-1 capitalize" data-testid={`import-job-status-${job.id}`}>{job.status} · {job.attempts} attempt(s){job.result?.pdf_fetch ? ` · ${job.result.pdf_fetch.replaceAll("_", " ")}` : ""}</p>
            {job.last_error && <p className="text-xs text-red-700 mt-1 break-words" data-testid={`import-job-error-${job.id}`}>{job.last_error}</p>}
          </div>)}
        </div>}
      </>}
    </section>
  );
};